import axios from 'axios'
import router from '../router'

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

console.log('🔧 API 配置:')
console.log('  - VITE_API_URL:', import.meta.env.VITE_API_URL)
console.log('  - API_URL:', API_URL)

export const authApi = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  retry: 3,
  retryDelay: 1000
})

console.log('  - authApi baseURL:', authApi.defaults.baseURL)

// 添加请求拦截器
authApi.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 添加响应拦截器
authApi.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if ((error.code === 'ECONNABORTED' || !error.response) && originalRequest.retry) {
      originalRequest._retry = (originalRequest._retry || 0) + 1
      
      if (originalRequest._retry <= originalRequest.retry) {
        await new Promise(resolve => setTimeout(resolve, originalRequest.retryDelay))
        console.log(`重试请求 (${originalRequest._retry}/${originalRequest.retry})`)
        return authApi(originalRequest)
      }
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('userCredentials')
      
      // 当token过期或无效时，自动重定向到登录页面
      if (router.currentRoute.value.path !== '/login') {
        router.push('/login')
      }
    }

    return Promise.reject(error)
  }
)

export const login = async (username, password) => {
  try {
    console.log('尝试登录:', { username })
    const response = await authApi.post('/login', { username, password })
    console.log('登录响应:', response.data)
    return response.data
  } catch (error) {
    console.error('登录错误:', error)
    if (!error.response) {
      throw new Error('网络连接失败，请检查网络后重试')
    }
    throw error.response?.data || { message: '登录失败，请稍后重试' }
  }
}

// 上传头像
export const uploadAvatar = async (file) => {
  const formData = new FormData()
  formData.append('avatar', file)

  try {
    const response = await authApi.post('/users/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    return response.data
  } catch (error) {
    throw error.response?.data || { message: '网络错误' }
  }
}

// 获取用户头像
export const getUserAvatar = async (username) => {
  try {
    const fullUrl = `/users/avatar/${username}`
    console.log('请求头像API:', `${API_URL}${fullUrl}`)

    const response = await authApi.get(fullUrl, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    
    if (response.data?.avatarUrl) {
      // 确保返回的URL不包含undefined
      response.data.avatarUrl = response.data.avatarUrl.replace('undefined', '')
    }
    
    console.log('处理后的头像URL:', response.data?.avatarUrl)
    return response.data
  } catch (error) {
    console.error('API错误详情:', error.response || error)
    throw error.response?.data || { message: '网络错误' }
  }
}

// 创建一个带认证的axios实例供其他组件使用
export const createAuthApi = (token) => {
  // 移除 baseURL 中的 /api 后缀，因为组件中的调用已经包含了 /api
  const baseUrl = API_URL.replace(/\/api$/, '')

  const api = axios.create({
    baseURL: baseUrl,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
  
  // 为新创建的实例也添加响应拦截器
  api.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        if (router.currentRoute.value.path !== '/login') {
          router.push('/login')
        }
      }
      return Promise.reject(error)
    }
  )
  
  // 添加请求拦截器
  api.interceptors.request.use(config => {
    config.metadata = { startTime: new Date() }
    console.log(`🚀 开始请求: ${config.url}`, config)
    return config
  })

  // 添加响应拦截器
  api.interceptors.response.use(response => {
    const duration = new Date() - response.config.metadata.startTime
    console.log(`✅ 请求完成: ${response.config.url}`, {
      duration: `${duration}ms`,
      status: response.status,
      size: JSON.stringify(response.data).length
    })
    return response
  })
  
  return api
}

// 修改发货相关的API
export const shipLabels = async (shipData) => {
  try {
    console.log('API发送数据:', shipData)
    const response = await authApi.post('/print-tasks/ship', shipData, {  // 直接发送 shipData
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    console.log('API响应:', response.data)
    return response.data
  } catch (error) {
    console.error('API错误:', error.response || error)
    throw error.response?.data || { message: '网络错误' }
  }
}

// 添加更新用户的专门方法
export const updateUser = async (originalUsername, userData) => {
  try {
    const response = await authApi.put(`/users/${originalUsername}`, userData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    })
    return response.data
  } catch (error) {
    console.error('更新用户失败:', error.response || error)
    throw error.response?.data || { message: '更新用户失败' }
  }
}